import { Track } from "@/src/lib/tracking";
/**
 * 兑换码状态管理Hook
 * 管理兑换流程的状态和交互逻辑
 */

import { useState, useCallback, useMemo } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { Reward } from "./useQueryRewards";
import { useRedeemCode, type RedeemResult } from "./useRedeemCode";
import Translations from "@translate/hooks/useRedeemCodeState";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface RedeemCodeState {
  inputCode: string;
  isInputValid: boolean;
  isRedeeming: boolean;
  redeemResult: RedeemResult | null;
  error: string | null;
  validationError: string | null;
  isSuccess: boolean;
  successRewards: Reward[];
}

export interface UseRedeemCodeStateResult extends RedeemCodeState {
  handleInputChange: (value: string) => void;
  handleInputBlur: () => void;
  handleRedeem: () => Promise<RedeemResult | null>;
  resetState: () => void;
  canRedeem: boolean;
  hasError: boolean;
}

export const useRedeemCodeState = (
  initCode?: string | null,
): UseRedeemCodeStateResult => {
  const [inputCode, setInputCode] = useState(initCode || "");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [redeemResult, setRedeemResult] = useState<RedeemResult | null>(null);

  const { validateCodeFormat, redeemCode, isLoading, errorCode } =
    useRedeemCode();

  const handleInputChange = useCallback((value: string) => {
    setInputCode(value);
    setValidationError(null);
    setRedeemResult(null);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (inputCode.trim()) {
      const validation = validateCodeFormat(inputCode);
      if (!validation.isValid) {
        setValidationError(I18n.t("Invalid Code Format."));
      } else {
        setValidationError(null);
      }
    }
  }, [inputCode, validateCodeFormat]);

  const handleRedeem = useCallback(async () => {
    if (!inputCode.trim()) {
      setValidationError("Please enter a code.");
      OspToast.warning({
        content: "Please enter a code.",
      });
      return null;
    }

    const validation = validateCodeFormat(inputCode);
    if (!validation.isValid) {
      setValidationError(I18n.t("Invalid Code Format."));
      return null;
    }
    setValidationError(null);
    try {
      Track.log("info", "useRedeemCodeState", "Starting redeem process", {
        code: inputCode,
      });

      const result = await redeemCode(inputCode);
      setRedeemResult(result);

      if (result.success) {
        setInputCode("");
        setValidationError(null);
        Track.log("info", "useRedeemCodeState", "Redeem successful", {
          rewards: String(result.rewards),
        });
        return result;
      } else {
        return result;
      }
    } catch (err) {
      Track.error(err, { tag: "useRedeemCodeState", msg: "Redeem error:" });
      OspToast.error({
        content: "Network error. Please try again.",
      });
      return null;
    }
  }, [inputCode, validateCodeFormat, redeemCode]);

  const resetState = useCallback(() => {
    setInputCode("");
    setValidationError(null);
    setRedeemResult(null);
  }, []);

  const isInputValid = useMemo(() => {
    if (!inputCode.trim()) {
      return false;
    }
    const validation = validateCodeFormat(inputCode);
    return validation.isValid;
  }, [inputCode, validateCodeFormat]);

  const isRedeeming = isLoading;
  const isSuccess = redeemResult?.success || false;
  const successRewards = redeemResult?.success ? redeemResult.rewards : [];

  const hasError = useMemo(() => {
    return !!(validationError || (redeemResult && !redeemResult.success));
  }, [validationError, redeemResult]);

  const canRedeem = useMemo(() => {
    return inputCode.trim() !== "" && !isRedeeming && !hasError;
  }, [hasError, inputCode, isRedeeming]);

  const currentError = useMemo(() => {
    if (validationError) {
      return validationError;
    }
    if (errorCode === 186011) {
      return I18n.t("Code verification failed.");
    }
    if (errorCode === 186013) {
      return I18n.t("Redemption Failed.");
    }
    if (errorCode === 180888) {
      return I18n.t("Too many requests, please try again later.");
    }
    if (errorCode === 186014) {
      return I18n.t("Ineligible user status for this code.");
    }
    if (errorCode === 186015) {
      return I18n.t("Requirements not met to generate code.");
    }
    if (errorCode === 186016) {
      return I18n.t("This code has reached its usage limit.");
    }
    if (errorCode === 186017) {
      return I18n.t("Redemption time requirement not met.");
    }
    if (errorCode === 186018) {
      return I18n.t("You have reached the redemption limit.");
    }

    return null;
  }, [validationError, errorCode]);

  return {
    inputCode,
    isInputValid,
    isRedeeming,
    redeemResult,
    error: currentError,
    validationError,
    isSuccess,
    successRewards,
    handleInputChange,
    handleInputBlur,
    handleRedeem,
    resetState,
    canRedeem,
    hasError,
  };
};
